import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  HelpCircle,
  Activity,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { getPlatformStatus } from "@/lib/status.functions";
import { LEVEL_LABELS, OVERALL_HEADLINES, type StatusLevel } from "@/lib/status-shared";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/status")({
  head: () => ({
    meta: [
      { title: "Status da plataforma · Beit Tefilah Rechovot" },
      {
        name: "description",
        content:
          "Acompanhe em tempo real a disponibilidade dos sistemas da área de membros da Beit Tefilah Rechovot.",
      },
      { property: "og:title", content: "Status da plataforma · Beit Tefilah Rechovot" },
      {
        property: "og:description",
        content: "Disponibilidade de login, catálogo, sincronização e notificações da plataforma.",
      },
    ],
  }),
  component: StatusPage,
});

const STYLES: Record<StatusLevel, { icon: typeof CheckCircle2; badge: string; dot: string }> = {
  operational: {
    icon: CheckCircle2,
    badge: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
    dot: "bg-emerald-500",
  },
  degraded: {
    icon: AlertTriangle,
    badge: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
    dot: "bg-amber-500",
  },
  down: {
    icon: XCircle,
    badge: "bg-destructive/10 text-destructive",
    dot: "bg-destructive",
  },
  unknown: {
    icon: HelpCircle,
    badge: "bg-muted text-muted-foreground",
    dot: "bg-muted-foreground",
  },
};

function formatDateTime(iso: string) {
  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));
}

function StatusPage() {
  const fetchStatus = useServerFn(getPlatformStatus);
  const { data, isLoading, isFetching, refetch, error } = useQuery({
    queryKey: ["platform-status"],
    queryFn: () => fetchStatus({ data: {} as any }),
    refetchInterval: 60_000,
  });

  const overall: StatusLevel = data?.overall ?? "unknown";
  const OverallIcon = STYLES[overall].icon;

  return (
    <div className="mx-auto max-w-4xl space-y-8">
      <header className="text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-gold/15">
          <Activity className="h-7 w-7 text-gold-foreground" />
        </div>
        <h1 className="mt-4 font-serif text-3xl font-semibold text-primary sm:text-4xl">
          Status da plataforma
        </h1>
        <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground sm:text-base">
          Disponibilidade dos sistemas que fazem a área de membros funcionar. A página atualiza
          automaticamente a cada minuto.
        </p>
      </header>

      <section
        className={cn(
          "flex flex-col gap-3 rounded-xl border border-border bg-card p-5 sm:flex-row sm:items-center sm:justify-between",
        )}
      >
        <div className="flex items-start gap-3">
          <OverallIcon
            className={cn(
              "mt-0.5 h-6 w-6 shrink-0",
              overall === "operational" && "text-emerald-600 dark:text-emerald-400",
              overall === "degraded" && "text-amber-600 dark:text-amber-400",
              overall === "down" && "text-destructive",
              overall === "unknown" && "text-muted-foreground",
            )}
          />
          <div>
            <p className="font-serif text-lg font-semibold text-primary">
              {isLoading ? "Verificando sistemas..." : OVERALL_HEADLINES[overall]}
            </p>
            <p className="text-xs text-muted-foreground sm:text-sm">
              {data ? `Última verificação: ${formatDateTime(data.checkedAt)}` : "—"}
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={cn("mr-2 h-4 w-4", isFetching && "animate-spin")} />
          Verificar agora
        </Button>
      </section>

      {error && (
        <p className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          Não foi possível carregar o status agora. Tente novamente em instantes.
        </p>
      )}

      <section className="space-y-3">
        <h2 className="font-serif text-xl font-semibold text-primary">Sistemas</h2>
        <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-card">
          {(data?.components ?? []).map((c) => {
            const s = STYLES[c.level];
            return (
              <li
                key={c.key}
                className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={cn("h-2.5 w-2.5 shrink-0 rounded-full", s.dot)} />
                    <p className="truncate font-medium text-foreground">{c.name}</p>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground sm:text-sm">{c.description}</p>
                  <p className="mt-1 break-words text-xs text-muted-foreground/90">{c.detail}</p>
                </div>
                <span
                  className={cn(
                    "shrink-0 self-start rounded-full px-3 py-1 text-xs font-semibold sm:self-center",
                    s.badge,
                  )}
                >
                  {LEVEL_LABELS[c.level]}
                </span>
              </li>
            );
          })}
          {isLoading &&
            Array.from({ length: 5 }).map((_, i) => (
              <li key={i} className="p-4">
                <div className="h-4 w-40 animate-pulse rounded bg-muted" />
                <div className="mt-2 h-3 w-60 animate-pulse rounded bg-muted" />
              </li>
            ))}
        </ul>
      </section>

      <section className="space-y-3">
        <h2 className="font-serif text-xl font-semibold text-primary">Incidentes recentes (48h)</h2>
        {data && data.incidents.length === 0 ? (
          <p className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
            Nenhum incidente registrado nas últimas 48 horas.
          </p>
        ) : (
          <ul className="space-y-2">
            {(data?.incidents ?? []).map((inc, i) => (
              <li key={i} className="rounded-xl border border-border bg-card p-4 text-sm">
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      "h-2 w-2 rounded-full",
                      inc.severity === "error" ? "bg-destructive" : "bg-amber-500",
                    )}
                  />
                  <span className="text-xs text-muted-foreground">{formatDateTime(inc.when)}</span>
                </div>
                <p className="mt-1 break-words text-foreground">{inc.message}</p>
              </li>
            ))}
          </ul>
        )}
      </section>

      <p className="text-center text-xs text-muted-foreground">
        Falhas identificadas aqui geram automaticamente um aviso por e-mail aos administradores da
        plataforma.
      </p>
    </div>
  );
}
